import { useState } from 'react';
import type { RegionOption, YearOption } from '@/types/movie';
import type { SortKey } from '@/utils/movie';

interface FilterPanelProps {
  regionOptions: RegionOption[];
  yearOptions: YearOption[];
  regionsSelected: string[];
  yearsSelected: string[];
  sort: SortKey;
  onToggleRegion: (key: string) => void;
  onToggleYear: (key: string) => void;
  onSortChange: (key: SortKey) => void;
  onReset: () => void;
  activeCount: number;
}

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'year_desc', label: '最新上映' },
  { key: 'year_asc', label: '上映最早' },
  { key: 'rating_desc', label: '评分最高' },
];

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`whitespace-nowrap rounded-full border px-3.5 py-1.5 text-sm transition-colors cursor-pointer ${
        active
          ? 'border-primary-500 bg-primary-500 text-background-50'
          : 'border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300'
      }`}
    >
      {children}
    </button>
  );
}

function FilterContent(props: FilterPanelProps) {
  const {
    regionOptions,
    yearOptions,
    regionsSelected,
    yearsSelected,
    sort,
    onToggleRegion,
    onToggleYear,
    onSortChange,
    onReset,
  } = props;

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-3 text-sm font-bold text-foreground-800">按地区筛选</h4>
        <div className="flex flex-wrap gap-2">
          {regionOptions.map((r) => (
            <Pill key={r.key} active={regionsSelected.includes(r.key)} onClick={() => onToggleRegion(r.key)}>
              {r.name}
            </Pill>
          ))}
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-sm font-bold text-foreground-800">按年份筛选</h4>
        <div className="flex flex-wrap gap-2">
          {yearOptions.map((y) => (
            <Pill key={y.key} active={yearsSelected.includes(y.key)} onClick={() => onToggleYear(y.key)}>
              {y.name}
            </Pill>
          ))}
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-sm font-bold text-foreground-800">排序方式</h4>
        <div className="flex flex-wrap gap-2">
          {sortOptions.map((s) => (
            <Pill key={s.key} active={sort === s.key} onClick={() => onSortChange(s.key)}>
              {s.label}
            </Pill>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={onReset}
        className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-600 transition-colors hover:border-accent-400 hover:text-accent-600 cursor-pointer"
      >
        <i className="ri-refresh-line text-base"></i>
        重置筛选
      </button>
    </div>
  );
}

export default function FilterPanel(props: FilterPanelProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { activeCount } = props;

  return (
    <>
      {/* Desktop inline panel */}
      <aside className="hidden w-full shrink-0 lg:block lg:w-[280px]">
        <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
          <FilterContent {...props} />
        </div>
      </aside>

      {/* Mobile trigger */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex w-full items-center justify-between rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-lg text-primary-500"></i>
            筛选与排序
          </span>
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1.5 text-xs font-bold text-white">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">筛选与排序</h3>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <FilterContent {...props} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-6 w-full whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-bold text-background-50 cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </>
  );
}